import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useJobsite } from "../context/JobsiteContext";
import UserAvatar from "../components/UserAvatar";
import LocationMap from "../components/LocationMap";
import { Building2, ChevronLeft, ChevronRight, ExternalLink, Mail, MapPin, Pencil, Phone } from "lucide-react";

const ROLE_LABEL = { admin: "Admin", supervisor: "Supervisor" };

function isIOS() {
  try {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  } catch (e) {
    return false;
  }
}

// Abre o endereco no app de mapas do aparelho (Apple Maps no iPhone, Google Maps nos outros).
function mapsUrl(site) {
  const lat = Number(site.lat);
  const lng = Number(site.lng);
  const hasCoords = Boolean(lat) && Boolean(lng);
  const address = [site.address, site.state, site.postcode].filter(Boolean).join(", ");
  if (isIOS()) {
    return hasCoords
      ? `https://maps.apple.com/?ll=${lat},${lng}&q=${encodeURIComponent(site.name || "Jobsite")}`
      : `https://maps.apple.com/?q=${encodeURIComponent(address)}`;
  }
  return `https://www.google.com/maps/search/?api=1&query=${hasCoords ? `${lat},${lng}` : encodeURIComponent(address)}`;
}

const isWebLink = (url) => {
  const u = String(url || "").toLowerCase();
  return u.startsWith("http://") || u.startsWith("https://");
};

function SectionTitle({ title, open, onToggle, testId }) {
  return (
    <div className="mt-9 flex items-center justify-between">
      <h2 className="text-2xl font-bold tracking-tight">{title}</h2>
      <button type="button" onClick={onToggle} className="px-1 py-2 text-base font-semibold text-primary" data-testid={testId}>
        {open ? "Hide" : "Show"}
      </button>
    </div>
  );
}

export default function ProjectInfo() {
  const navigate = useNavigate();
  const { user, company } = useAuth();
  const { activeSite } = useJobsite();
  const isAdmin = user?.role === "admin" || Boolean(user?.is_master_admin);
  const siteId = activeSite ? activeSite.id : null;

  const [site, setSite] = useState(activeSite || null);
  const [failed, setFailed] = useState(false);
  const [showTeam, setShowTeam] = useState(true);
  const [showLinks, setShowLinks] = useState(true);
  const [openPerson, setOpenPerson] = useState("");

  // Sempre busca a obra de novo: assim a equipe e os links aparecem atualizados.
  useEffect(() => {
    if (siteId === null) return undefined;
    let cancelled = false;
    setFailed(false);
    api
      .get("/jobsites")
      .then(({ data }) => {
        if (cancelled) return;
        const found = Array.isArray(data) ? data.find((s) => s.id === siteId) : null;
        if (found) setSite(found);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  const back = () => (window.history.length > 1 ? navigate(-1) : navigate("/app"));

  const current = site && siteId !== null && site.id === siteId ? site : activeSite;
  const team = Array.isArray(current?.team) ? current.team : [];
  const links = Array.isArray(current?.links) ? current.links.filter((l) => isWebLink(l.url)) : [];
  const address = current ? [current.address, current.state, current.postcode].filter(Boolean).join(", ") : "";
  const hasCoords = current ? Boolean(Number(current.lat)) && Boolean(Number(current.lng)) : false;

  return (
    <div className="mx-auto max-w-2xl px-4 pb-28 pt-4 md:px-6 md:pb-12 md:pt-8" data-testid="project-info">
      <div className="flex items-center gap-2">
        <button type="button" onClick={back} aria-label="Back" className="grid h-11 w-11 shrink-0 place-items-center rounded-full active:bg-accent" data-testid="pi-back">
          <ChevronLeft className="h-7 w-7" />
        </button>
        <h1 className="flex-1 pr-11 text-center text-xl font-bold">Project information</h1>
      </div>

      {!current ? (
        <div className="mt-10 rounded-2xl border border-border bg-card p-6 text-center" data-testid="pi-nosite">
          <Building2 className="mx-auto h-8 w-8 text-muted-foreground" />
          <div className="mt-3 text-lg font-bold">No project selected</div>
          <div className="mt-1 text-sm text-muted-foreground">Choose a project in the bar at the top of the screen.</div>
        </div>
      ) : (
        <>
          <div className="mt-5 flex items-center gap-4">
            <div className="grid h-20 w-20 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-primary/30 to-primary/5 text-primary">
              <Building2 className="h-9 w-9" />
            </div>
            <div className="min-w-0">
              {company?.name ? <div className="truncate text-sm text-muted-foreground">{company.name}</div> : null}
              <div className="break-words text-xl font-bold leading-snug" data-testid="pi-name">
                {current.name}
              </div>
            </div>
          </div>

          {failed && (
            <div className="mt-4 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-500" data-testid="pi-offline">
              Could not refresh. Showing what was saved on this phone.
            </div>
          )}

          <h2 className="mt-8 text-2xl font-bold tracking-tight">Location</h2>
          <div className="mt-3">
            {hasCoords ? (
              <LocationMap user={null} site={{ lat: Number(current.lat), lng: Number(current.lng), radius_m: current.radius_m, name: current.name }} height={260} />
            ) : (
              <div className="grid h-40 place-items-center rounded-lg border border-dashed border-border bg-muted/30 px-6 text-center text-sm text-muted-foreground" data-testid="pi-nomap">
                This project has no map location yet.
              </div>
            )}
          </div>
          {address || hasCoords ? (
            <a
              href={mapsUrl(current)}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 flex items-start gap-2 text-lg font-medium leading-snug text-primary"
              data-testid="pi-address"
            >
              <MapPin className="mt-1 h-5 w-5 shrink-0" />
              <span className="break-words">{address || "Open in Maps"}</span>
            </a>
          ) : (
            <div className="mt-3 text-sm text-muted-foreground">No address has been added yet.</div>
          )}

          <SectionTitle title="Project team" open={showTeam} onToggle={() => setShowTeam((v) => !v)} testId="pi-toggle-team" />
          {showTeam && (
            <ul className="mt-2" data-testid="pi-team">
              {team.length === 0 && (
                <li className="py-4 text-sm text-muted-foreground" data-testid="pi-team-empty">
                  {isAdmin ? "No one has been chosen yet. Pick the team in Jobsites." : "No team members have been added yet."}
                </li>
              )}
              {team.map((p) => {
                const open = openPerson === p.id;
                const tel = String(p.phone || "").replace(/[^0-9+]/g, "");
                return (
                  <li key={p.id} className="border-b border-border last:border-0">
                    <button
                      type="button"
                      onClick={() => setOpenPerson(open ? "" : p.id)}
                      aria-expanded={open}
                      className="flex w-full items-center gap-4 py-4 text-left"
                      data-testid={`team-${p.id}`}
                    >
                      <UserAvatar url={p.avatar_url} name={p.name} size={48} />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-lg font-semibold">{p.name}</div>
                        <div className="text-base text-muted-foreground">{ROLE_LABEL[p.role] || p.role}</div>
                      </div>
                      <ChevronRight className={`h-5 w-5 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-90" : ""}`} />
                    </button>
                    {open && (
                      <div className="flex flex-wrap gap-2 pb-4 pl-16" data-testid={`team-actions-${p.id}`}>
                        {tel ? (
                          <a href={`tel:${tel}`} className="inline-flex h-11 items-center gap-2 rounded-full border border-border px-4 text-sm font-semibold active:bg-accent">
                            <Phone className="h-4 w-4" /> Call
                          </a>
                        ) : null}
                        {p.email ? (
                          <a href={`mailto:${p.email}`} className="inline-flex h-11 items-center gap-2 rounded-full border border-border px-4 text-sm font-semibold active:bg-accent">
                            <Mail className="h-4 w-4" /> Email
                          </a>
                        ) : null}
                        {!tel && !p.email ? <span className="py-2 text-sm text-muted-foreground">No contact details yet.</span> : null}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          <SectionTitle title="Project links" open={showLinks} onToggle={() => setShowLinks((v) => !v)} testId="pi-toggle-links" />
          {showLinks && (
            <ul className="mt-2" data-testid="pi-links">
              {links.length === 0 && (
                <li className="py-4 text-sm text-muted-foreground" data-testid="pi-links-empty">
                  {isAdmin ? "No links yet. Add them in Jobsites." : "No links have been added yet."}
                </li>
              )}
              {links.map((l, i) => (
                <li key={`${l.url}-${i}`} className="border-b border-border last:border-0">
                  <a
                    href={l.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between gap-3 py-4 text-lg font-medium text-primary"
                    data-testid={`link-${i}`}
                  >
                    <span className="min-w-0 break-words">{l.title || l.url}</span>
                    <ExternalLink className="h-5 w-5 shrink-0" />
                  </a>
                </li>
              ))}
            </ul>
          )}

          {isAdmin && (
            <button
              type="button"
              onClick={() => navigate("/app/admin/jobsites")}
              className="mt-8 inline-flex h-12 items-center gap-2 rounded-full border border-border px-5 text-sm font-semibold active:bg-accent"
              data-testid="pi-edit"
            >
              <Pencil className="h-4 w-4" /> Edit team and links
            </button>
          )}
        </>
      )}
    </div>
  );
}
